import { Divider, Typography } from '@scale/scaleui';
import { memo, useMemo } from 'react';
import { getTwoTextDiffContent } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';
import UtilitiesWrapper from 'react-scale/components/scalers/tasks/common/UtilitiesWrapper';
import { twMerge } from 'tailwind-merge';

interface TwoTextDiffViewProps {
  className?: string;
  textOne?: string;
  textTwo?: string;
  textOneTitle: string;
  textTwoTitle: string;
  // 'compact' allows the diff view to have a fixed height
  // 'stretch' forces the diff view to have a height that stretches to the height of the content
  variant?: 'compact' | 'stretch';
  enableRTL?: boolean;
}

const TwoTextSxSDiffView = memo(
  ({
    className,
    textOne,
    textTwo,
    textOneTitle,
    textTwoTitle,
    variant = 'compact',
    enableRTL = false,
  }: TwoTextDiffViewProps) => {
    const diffResult = useMemo(() => getTwoTextDiffContent(textOne, textTwo), [textOne, textTwo]);
    const diffResultNewText = diffResult?.diffResultNewText ?? '';
    const diffResultOriginalText = diffResult?.diffResultOriginalText ?? '';

    // We need to switch the left and right title text if RTL is enabled
    const [leftTitle, rightTitle] = enableRTL
      ? [textTwoTitle, textOneTitle]
      : [textOneTitle, textTwoTitle];

    return (
      <div
        className={twMerge(
          'relative flex flex-col w-full border border-neutral-200 overflow-hidden bg-neutral-0 rounded-sm items-stretch',
          variant === 'compact' ? 'h-96' : 'h-full',
          className,
        )}
      >
        {variant === 'compact' && (
          <div className="absolute left-1/2 top-0 bottom-0">
            <Divider direction="vertical" />
          </div>
        )}
        <div className="flex flex-row w-full border-b border-b-neutral-200">
          <div className="flex w-6/12 pl-3 h-9 items-center">
            <Typography.Subtitle1 className="text-neutral-500 text-xs font-medium">
              {leftTitle}
            </Typography.Subtitle1>
          </div>
          {variant === 'stretch' && <Divider direction="vertical" className="h-9" />}
          <div className="flex w-6/12 pl-3 h-9 items-center">
            <Typography.Subtitle1 className="text-neutral-500 text-xs font-medium">
              {rightTitle}
            </Typography.Subtitle1>
          </div>
        </div>

        <UtilitiesWrapper className="w-full h-full" enableRTL={enableRTL}>
          <div className="flex flex-row w-full h-full overflow-y-scroll">
            <div className="w-6/12 h-full text-neutral-900 text-sm whitespace-pre-wrap p-3 break-words">
              {diffResultOriginalText}
            </div>
            {variant === 'stretch' && (
              <div className="items-stretch">
                <Divider direction="vertical" />
              </div>
            )}
            <div className="w-6/12 h-full text-neutral-900 text-sm whitespace-pre-wrap p-3 break-words">
              {diffResultNewText}
            </div>
          </div>
        </UtilitiesWrapper>
      </div>
    );
  },
);

TwoTextSxSDiffView.displayName = 'TwoTextSxSDiffView';

export default TwoTextSxSDiffView;
